import {LayerController} from './layer.controller';
import {NGSIResult} from '../../shared/models/ngsi/ngsi-result';
import {catchError, forkJoin, Observable, of, take} from 'rxjs';
import {map, switchMap} from 'rxjs/operators';
import {Layer} from '../../shared/models/layer.model';
import {Page} from '../../shared/models/page.model';
import {Injector} from '@angular/core';
import {Marker} from '../../shared/models/map/marker.model';
import {NGSIQuery} from '../../shared/models/ngsi/ngsi-query.model';
import {QueryOptions} from '../../shared/models/ngsi/query-options';
import {NGSIEntity} from '../../shared/utils/ngsi-result-util';
import {EntityRepresentation} from '../../shared/models/visualization/representation.model';
import {NGSIEventType, Notification} from '../../shared/models/notification.model';
import {TcddLayerService} from '../services/data/tcdd-layer.service';
import {NGSIFilter} from '../../shared/models/ngsi/ngsi-filter.model';

export class TcddTrainsLayerController extends LayerController {
  displaysTooltip: boolean = true;
  private tcddLayerService: TcddLayerService;
  private readonly entityRep: EntityRepresentation[];
  constructor(layer: Layer, page: Page, private injector: Injector) {
    super(layer, page, injector);
    this.entityRep = layer.representations;
    this.tcddLayerService = TcddLayerService.getInstance(layer, injector);
    this.tcddLayerService.addSubscriber(this);
  }

  onDestroy(): void {
    super.onDestroy();
    this.tcddLayerService.unsubscribe(this);
  }


  protected getDialogContext(result: NGSIResult): any {
    const activeTrainId =  result.getEntityResult().entity.activeTrainId.value ? result.getEntityResult().entity.activeTrainId.value :
      result.getEntityResult().entity.activeTrainId[0].value;
    const passiveTrainId = result.getEntityResult().entity.passiveTrainId.value ? result.getEntityResult().entity.passiveTrainId.value :
      result.getEntityResult().entity.passiveTrainId[0].value;
    const trainNo = activeTrainId.substring(activeTrainId.lastIndexOf(':') + 1);

    const activeTrainQuery = this.cbrokerService
      .getEntity(this.getTrainQuery(activeTrainId), new QueryOptions(false)).pipe(catchError(() => of(null)));
    const passiveTrainQuery = passiveTrainId !== '-'
      ? this.cbrokerService.getEntity(this.getTrainQuery(passiveTrainId), new QueryOptions(false)).pipe(catchError(() => of(null)))
      : of(null);

    const activeDeviceStatusQuery = this.cbrokerService
      .getEntity(this.getDeviceStatus(activeTrainId), new QueryOptions(false, false, false, true)).pipe(catchError(() => of(null)));
    const passiveDeviceStatusQuery = passiveTrainId !== '-'
      ? this.cbrokerService.getEntity(this.getDeviceStatus(passiveTrainId), new QueryOptions(false, false, false, true)).pipe(catchError(() => of(null)))
      : of(null);

    const activeTrainStatusQuery = this.cbrokerService
      .getEntity(this.getTrainStatus(activeTrainId), new QueryOptions(false, false, false, true)).pipe(catchError(() => of(null)));
    const passiveTrainStatusQuery = passiveTrainId !== '-'
      ? this.cbrokerService.getEntity(this.getTrainStatus(passiveTrainId), new QueryOptions(false, false, false, true)).pipe(catchError(() => of(null)))
      : of(null);

    const activeTrainOfWagonQuery = this.cbrokerService
      .getEntity(this.getAllWagon(activeTrainId), new QueryOptions(false))
      .pipe(
        catchError(() => of(null)),
        switchMap(activeWagonResponse => {
          if (activeWagonResponse && activeWagonResponse.getEntityResult().entity.length > 0) {
            const wagonIds = activeWagonResponse.getEntityResult().entity
              .map((wagon, index, array) => {
                if (index === 0) {
                  return `${wagon.id}'`;
                } else if (index === array.length - 1) {
                  return `'${wagon.id}`;
                }
                return `'${wagon.id}'`;
              })
              .join(',');

            const wagonIdArray = wagonIds.split(',').map(id => id.replace(/^'+|'+$/g, '').trim());
            return forkJoin(
              wagonIdArray.map(wagonId =>
                this.cbrokerService
                  .getEntity(this.getWagonStatus(wagonId), new QueryOptions(false, false, false, true))
                  .pipe(catchError(() => of(null)))
              )
            ).pipe(
              map((wagonStatusResponses: any[]) => ({
                wagons: activeWagonResponse.getEntityResult().entity,
                wagonStatus: wagonStatusResponses
                  .filter(res => res !== null)
                  .map(res => res.getEntityResult().entity)
              }))
            );
          } else {
            return of({wagons: null, wagonStatus: null});
          }
        })
      );

    const passiveTrainOfWagonQuery = passiveTrainId !== '-' ? this.cbrokerService
      .getEntity(this.getAllWagon(passiveTrainId), new QueryOptions(false))
      .pipe(
        catchError(() => of(null)),
        switchMap(passiveWagonResponse => {
          if (passiveWagonResponse && passiveWagonResponse.getEntityResult().entity.length > 0) {
            const wagonIds = passiveWagonResponse.getEntityResult().entity
              .map((wagon, index, array) => {
                if (index === 0) {
                  return `${wagon.id}'`;
                } else if (index === array.length - 1) {
                  return `'${wagon.id}`;
                }
                return `'${wagon.id}'`;
              })
              .join(',');

            const wagonIdArray = wagonIds.split(',').map(id => id.replace(/^'+|'+$/g, '').trim());
            return forkJoin(
              wagonIdArray.map(wagonId =>
                this.cbrokerService
                  .getEntity(this.getWagonStatus(wagonId), new QueryOptions(false, false, false, true))
                  .pipe(catchError(() => of(null)))
              )
            ).pipe(
              map((wagonStatusResponses: any[]) => ({
                wagons: passiveWagonResponse.getEntityResult().entity,
                wagonStatus: wagonStatusResponses
                  .filter(res => res !== null)
                  .map(res => res.getEntityResult().entity)
              }))
            );
          } else {
            return of({wagons: null, wagonStatus: null});
          }
        })
      ) : of(null);

    const contextObservable: Observable<any> = forkJoin([
      activeTrainQuery,
      passiveTrainQuery,
      activeDeviceStatusQuery,
      passiveDeviceStatusQuery,
      activeTrainOfWagonQuery,
      passiveTrainOfWagonQuery,
      activeTrainStatusQuery,
      passiveTrainStatusQuery
    ]).pipe(
      take(1),
      switchMap(response => {
        return of({
          position: result.getEntityResult().entity,
          activeTrain: response[0]?.getEntityResult().entity[0],
          passiveTrain: passiveTrainId !== '-' ? response[1]?.getEntityResult().entity[0] : null,
          activeDeviceStatus: response[2]?.getEntityResult().entity,
          passiveDeviceStatus: response[3]?.getEntityResult().entity,
          activeTrainWagons: response[4]?.wagons,
          activeTrainWagonStatus: response[4]?.wagonStatus,
          passiveTrainWagons: passiveTrainId !== '-' ? response[5]?.wagons : null,
          passiveTrainWagonStatus: response[5]?.wagonStatus,
          activeTrainStatus: response[6]?.getEntityResult().entity || null,
          passiveTrainStatus: response[7]?.getEntityResult().entity  || null
        });
      })
    );

    return {
      page: this.popupPage,
      entity: result.getEntityResult(),
      asyncContext: contextObservable
    };
  }

  protected beforeEntitiesProcessed(results: any[]): NGSIResult[] {
    const entitiesFound: Set<string> = new Set();
    const entitiesDiscarded: Set<string> = new Set();
    const finalResults: NGSIResult[] = [];
    results.forEach(result => {
      if ( result.getEntityResult().entity.isCoupling ? (!result.getEntityResult().entity.isCoupling[0].value || result.getEntityResult().entity.isActive[0].value)
        : !result.getEntityResult().entity.entity.isCoupling[0].value || result.getEntityResult().entity.entity.isActive[0].value) {
        const trainId: string =  result.getEntityResult().entity.activeTrainId[0].value;
        if (!entitiesFound.has(trainId) && !entitiesDiscarded.has(trainId)) {
          finalResults.push(result);
          entitiesFound.add(trainId);
        }
      } else { // filter passive train positions recorded before coupling
        const passiveTrainId: string =  result.getEntityResult().entity.passiveTrainId[0].value;
        if (!entitiesDiscarded.has(passiveTrainId)) {
          entitiesDiscarded.add(passiveTrainId);
        }
      }
    });
    return finalResults;
  }

  protected handleCreateEvent(representation: EntityRepresentation, notification: Notification): NGSIResult {
    const newEntityActiveTrainId: string = notification.eventPayload.content.activeTrainId.value;
    const representationEntities = this.entities.get(representation);
    const entityIndex = representationEntities ? representationEntities.findIndex(e => {
      return this.getTrainIdFromNGSIResult(e) === newEntityActiveTrainId;
    }) : -1;
    if (entityIndex !== -1) {
      this.entities.get(representation).splice(entityIndex, 1);
      this.markers.get(representation).splice(entityIndex, 1);
    }
    return super.handleCreateEvent(representation, notification);
  }

  private getTrainIdFromNGSIResult(ngsiResult: NGSIResult): string {
    return ngsiResult.getEntityResult().getSingleElementValue('activeTrainId');
  }

  private getPassiveTrainIdFromNGSIResult(ngsiResult: NGSIResult): string {
    return ngsiResult.getEntityResult().getSingleElementValue('passiveTrainId');
  }

  protected websocketCallback(representation: EntityRepresentation, notification: Notification): void {
    let isActive: boolean = true;
    if (notification.eventClass === 'NGSIEvent') {
      if (notification.eventPayload.eventType === NGSIEventType.CREATE) {
        const content = notification.eventPayload.content;
        isActive = content.isActive.value === 1;
      }
    }
    if (isActive) {
      if (this.entities.has(representation)) {
        const result: NGSIResult [] = this.entities.get(representation);
        for (let i = 0; i < result.length; i++) {
          if (this.controlObservedat(result[i], notification.eventPayload.content.location) &&
            this.controlActiveTrainId(result[i], notification.eventPayload.content.activeTrainId)) {
            super.websocketCallback(representation, notification);
            break;
          }
        }
      }
    }
  }

  private controlObservedat(lastLocation: any, newLocation: any): boolean {
    const lastObservedat = lastLocation?.getEntityResult()?.entity?.location?.observedAt ||
      lastLocation?.getEntityResult().entity?.location[0]?.observedAt;
    return lastObservedat <= newLocation.observedAt;
  }

  private controlActiveTrainId(resultTrainId: any, trainId: any): boolean {
    const currentTrainId = resultTrainId?.getEntityResult()?.entity?.activeTrainId?.value ||
      resultTrainId?.getEntityResult().entity?.activeTrainId[0]?.value;
    return currentTrainId === trainId.value;
  }

  getTcddCouplingPositions() {
    this.entityRep.forEach(entityRep => {
      this.processData(this.tcddLayerService.getTcddCouplingPositionForRepresentation(entityRep), entityRep);
    });
  }

  protected getTrainQuery(trainId) {
    return new NGSIQuery({
      filter: {
        type: [NGSIEntity.TCDDTrain],
        q: {
          jsonClass: 'NGSIQueryTerm',
          path: {
            jsonClass: 'NGSIPath',
            propOrRelPath: [
              'id'
            ],
            isTemporal: false,
            isPropOrRelArray: false
          },
          op: '==',
          value: [
            trainId
          ]
        }
      },
      limit: 1,
      lastNDays: 365
    });
  }

  protected getDeviceStatus(trainId) {
    const deviceStatusId = trainId.replace('TcddTrain', 'DeviceStatus');
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.id = [deviceStatusId];

    return query;
  }

  protected getTrainStatus(trainId) {
    const deviceStatusId = trainId.replace('TcddTrain', 'TrainStatus');
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.id = [deviceStatusId];

    return query;
  }

  protected getAllWagon(trainId) {
    return new NGSIQuery({
      filter: {
        type: [NGSIEntity.WAGON],
        q: {
          jsonClass: 'NGSIQueryTerm',
          path: {
            jsonClass: 'NGSIPath',
            propOrRelPath: [
              'trainRef'
            ],
            isTemporal: false,
            isPropOrRelArray: false
          },
          op: '==',
          value: [
            trainId
          ]
        }
      },
      lastNDays: 365
    });
  }

  protected getWagonStatus(wagonId) {
    const deviceStatusId = wagonId.replace('Wagon', 'WagonStatus');
    const query: NGSIQuery = new NGSIQuery();
    query.filter = new NGSIFilter();
    query.filter.id = [deviceStatusId];

    return query;
  }

  protected getSingleQuery(id, type) {
    return new NGSIQuery({
      filter: {
        type: [type],
        q: {
          jsonClass: 'NGSIQueryTerm',
          path: {
            jsonClass: 'NGSIPath',
            propOrRelPath: [
              'id'
            ],
            isTemporal: false,
            isPropOrRelArray: false
          },
          op: '==',
          value: [
            `urn:ngsi-ld:${type}:${id}`
          ]
        }
      },
      limit: 1,
      lastNDays: 365,
      sortField: 'observedAt',
      sortDirection: 'desc'
    });
  }

  createTooltip(marker: Marker): string {
    const trainNo = marker.entity.getSingleElementValue('activeHmiVehicleNo');
    const trainId = marker.entity.getSingleElementValue('activeTrainId');
    const tempTrainId =  trainId.substring(trainId.lastIndexOf(':') + 1);

    const passiveTrainNo = marker.entity.getSingleElementValue('passiveHmiVehicleNo');
    const passiveTrainId = marker.entity.getSingleElementValue('passiveTrainId');

    const speed = marker.entity.getSingleElementValue('speed');
    const distanceTraveled = marker.entity.getSingleElementValue('distanceTraveled');
    const isCoupling: boolean = marker.entity.getSingleElementValue('isCoupling');

    const trainNoLabel = this.translateService.instant(isCoupling ? 'leaderTrainNo' : 'trainNo');
    const trainIdLabel = this.translateService.instant(isCoupling ? 'leaderTrainId' : 'trainId');
    const speedLabel = this.translateService.instant( 'speed');
    const distanceTraveledLabel = this.translateService.instant('distanceTraveled');

    let html = '<div class="entity-tooltip">';
    html += `<div class="text"><span class="title">${trainNoLabel} : </span> ${trainNo} </div>`;
    html += `<div class="text"><span class="title">${trainIdLabel} : </span> ${tempTrainId} </div>`;

    if (isCoupling) {
      const tempPassiveTrainId = passiveTrainId.substring(passiveTrainId.lastIndexOf(':') + 1);
      const passiveTrainNoLabel = this.translateService.instant('followerTrainNo');
      const passiveTrainIdLabel = this.translateService.instant('followerTrainId');
      html += `<div class="text"><span class="title">${passiveTrainNoLabel} : </span> ${passiveTrainNo} </div>`;
      html += `<div class="text"><span class="title">${passiveTrainIdLabel} : </span> ${tempPassiveTrainId} </div>`;
    }
    html += `<div class="text"><span class="title">${speedLabel} : </span> ${speed}  km/s </div>`;
    html += `<div class="text"><span class="title">${distanceTraveledLabel} : </span> ${distanceTraveled} km </div>`;
    html += '</div>';

    return html;
  }

  processData(results: NGSIResult[], representation: EntityRepresentation) {
    results = this.beforeEntitiesProcessed(results);

    this.clearEntities(representation);
    this.entities.set(representation, results);
    this.createMapObjects(this.entities.get(representation), representation);

    if (this.initializedLayerCount < this.layer.representations.length) {
      this.setVisibility(true);
      this.initializedLayerCount++;
    }
    this.mergeMappedEntities();
    this.afterEntitiesProcessed();
    this.eventService.broadcastLayerProcessingFinishedEvent(this);
  }
}
